import React, { useState, useEffect, useContext } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from "reactstrap";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import InfoPopover from "components/CommonComponents/InfoPopover";
import { getAttendanceLock, updateAttendanceLock } from "action/attendanceLock";

const AttendanceEditPeriodModal = ({ isOpen, toggle }) => {
  const { notify } = useContext(NotificationContext);
  const [formData, setFormData] = useState({
    period: { value: "", error: "" }
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchLockPeriod();
    } else {
      setFormData({ period: { value: "", error: "" } });
      setLoading(false);
    }
  }, [isOpen]);

  const fetchLockPeriod = async () => {
    setLoading(true);
    try {
      const res = await getAttendanceLock();
      if (res?.code === 200) {
        const lockingDays = res?.data?.AttendanceLockingDays;
          
        if (lockingDays !== undefined) {
          setFormData({ period: { value: lockingDays.toString(), error: "" } });
        }
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg || JSON.stringify(res?.message)
          : res?.message || "Failed to fetch attendance edit period";
        notify("danger", message);
      }
    } catch (err) {
      notify("danger", err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (value.length > 2) return;
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
        error: "",
      },
    }));
  };

  const getValidationError = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    switch (name) {
      case "period":
        return !trimmedValue ? "Please enter attendance period" : "";
      default:
        return "";
    }
  };

  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const validateForm = () => {
    let newFormData = { ...formData };
    Object.keys(newFormData).forEach((key) => {
      newFormData[key] = {
        ...newFormData[key],
        error: getValidationError(key, newFormData[key].value),
      };
    });
    setFormData(newFormData);
    return Object.values(newFormData).every((item) => item.error === "");
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const payload = {
        attendanceLockingDays: parseInt(formData.period.value, 10)
      };
      const res = await updateAttendanceLock(payload);
      
      if (res?.code === 200) {
        notify("success", res?.message || "Attendance edit period updated successfully");
        toggle();
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg || JSON.stringify(res?.message)
          : res?.message || "Failed to update attendance edit period";
        notify("danger", message);
      }
    } catch (err) {
      notify("danger", err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle} className="border-0 pb-0">
        <div className="w-100">
          <div className="change-password-header">
            <i className="fa-regular fa-calendar change-password-icon mb-3" />
            <h2 className="mt-2 mb-0 text-deep-blue">Attendance Period</h2>
          </div>
        </div>
      </ModalHeader>
      <ModalBody className="pt-3">
        <div className="change-password-body">
          <FormGroup className="mb-0">
            <Label className="d-flex align-items-center">
              Edit Attendance Period (in days)
              <InfoPopover
                targetId="editPeriodInfo"
                content="Set the number of days during which trainers can update attendance after it has been added. Once this period expires, the attendance can no longer be edited."
              />
            </Label>
              <Input
                type="number"
                name="period"
                placeholder="Please enter attendance period"
                value={formData.period.value}
                min="0"
                onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                className={formData.period.error ? "is-invalid" : ""}
              />
            {formData.period.error && (
              <div className="invalid-feedback d-block">{formData.period.error}</div>
            )}
          </FormGroup>
        </div>
      </ModalBody>
      <ModalFooter className="pt-2">
        <Button color="secondary" onClick={toggle} className="save-btn-sm">
          Cancel
        </Button>
        <Button
          color="deep-blue"
          className="save-btn-sm"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? <i className="fa fa-spinner fa-spin" /> : "Save"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AttendanceEditPeriodModal;
